/**
 * [LEGAL PLACEHOLDER] Contracting entity is confirmed (GameCodex); the venue
 * (which state/country's courts, which state's law) is not, and is left
 * generic below. Do not treat this file as attorney-reviewed; it describes
 * real behavior accurately but the boilerplate needs a real legal pass before
 * this is load-bearing for a dispute.
 */
export function Terms({ onNavigate }: { onNavigate: (path: string) => void }) {
  return (
    <article className="legal">
      <a
        href="/"
        className="back"
        onClick={(e) => {
          e.preventDefault()
          onNavigate('/')
        }}
      >
        ← Back to Scratchyo
      </a>
      <h1>Terms &amp; Conditions</h1>
      <p className="updated">Last updated 2026-07-25</p>

      <p>
        Scratchyo is a fictional indie storefront from a television show — a parody, not
        affiliated with any real platform. By creating an account you agree to these terms.
      </p>

      <h2>1. Eligibility</h2>
      <p>You must be 18 or older to create an account or use Scratchyo.</p>

      <h2>2. Your account</h2>
      <p>
        Keep your credentials to yourself. You're responsible for what happens under your account.
        Accounts are managed by our shared GameCodex Accounts system.
      </p>

      <h2>3. No purchases, no charges</h2>
      <p>
        Scratchyo does not process payments and holds no payment credential. The "pay what you
        want" slider and checkout flow are part of the comedic premise: your account is real, but
        no checkout ever completes, no file is ever transferred, and you are never charged
        anything, regardless of the price you choose. See the AI Policy below for what the
        checkout flow does instead.
      </p>

      <h2>4. Acceptable use</h2>
      <p>Don't use Scratchyo to:</p>
      <ul>
        <li>attempt to disrupt, probe, or gain unauthorized access to the service;</li>
        <li>impersonate a real person or organization, or infringe their trademarks/IP;</li>
        <li>harass, threaten, or disclose private information about a real person;</li>
        <li>abuse or overload the account or checkout systems.</li>
      </ul>
      <p>We may suspend or terminate accounts that violate this.</p>

      <h2>5. AI Policy</h2>
      <p>Read this section before you check out — it's the part that's easy to miss.</p>
      <ul>
        <li>
          <strong>What happens automatically:</strong> the listing title, the price you choose, and
          (at checkout) your account email are sent to a third-party AI model (Claude, by
          Anthropic) via our backend, which generates the in-character reaction — the "Suggested
          Price" haggling over your number, then "the Cart" refusing to complete checkout. Nothing
          about either response is written or reviewed by a person before it's shown to you — it's
          fully automated.
        </li>
        <li>
          <strong>It's satire, not a factual claim.</strong> Neither persona is a genuine assessment
          of you, your generosity, or anything else — they're a deliberate bit, playing a haggler
          and a perpetually-broken checkout for laughs.
        </li>
        <li>
          <strong>Nothing you send is stored.</strong> Unlike some of our other portals, Scratchyo
          keeps no record of the prices you choose or the lines either AI persona generates —
          see the Privacy Policy for exactly what is and isn't kept.
        </li>
        <li>
          <strong>Don't submit sensitive information.</strong> This flow has no field meant for
          anything beyond a listing, a price, and your account email — don't put financial details,
          medical information, or government IDs anywhere in it.
        </li>
      </ul>

      <h2>6. No warranty</h2>
      <p>
        Scratchyo is provided "as is," for entertainment purposes, with no warranty of any kind. We
        don't guarantee the service will be available, error-free, or that AI output will be
        accurate, appropriate, or non-offensive — it's a parody generator, not a storefront that
        actually ships anything.
      </p>

      <h2>7. Limitation of liability</h2>
      <p>
        To the fullest extent permitted by law, Scratchyo / GameCodex is not liable for any
        indirect, incidental, or consequential damages arising from your use of the service.
      </p>

      <h2>8. Termination</h2>
      <p>We may suspend or terminate your account at any time for violating these terms.</p>

      <h2>9. Governing law</h2>
      <p>
        These terms are between you and GameCodex. [Placeholder — the specific state/country whose
        law and courts govern disputes is still to be confirmed.] Until finalized, disputes are
        intended to be handled under GameCodex's home jurisdiction, without regard to
        conflict-of-law principles.
      </p>

      <h2>10. Contact</h2>
      <p>
        Questions about these terms: <a href="mailto:legal@wogd.com">legal@wogd.com</a>.
      </p>
    </article>
  )
}
